import {
  DELETE,
  POST,
  PUT,

} from '../../constants/api';

import {
  API_ERROR_MESSAGES
} from '../../constants/constants';
/*
 * Base error class suitable for showing
 * to the end user.
 */
class BaseFetchError extends Error {
  /*
   * Create a new error
   * @param {array} messageLines - list of human-readable messages describing the error.
   * @param {object} body - JSON-decoded body returned by the API, if any.
   * @param {number} statusCode - HTTP status code.
   */
  constructor(messageLines, body, statusCode, url, ...args) {
    const bodyMessage = Object.keys(body || {}).map(k => body[k]).join('\n');
    const message = messageLines ? messageLines.join('\n') : bodyMessage;
    super(message, ...args);
    this.messageLines = messageLines || [bodyMessage];
    this.body = body;
    this.statusCode = parseInt(statusCode, 10);
  }
}

/*
 * Thrown as a result of crashing whatwg-fetch
 * (for example, due to connectivity error).
 * Adding an auto page refresh in the event this happens after 1 second to clear up the bad state and with a 60 second timestamp check to avoid a loop.
 */
export class NetworkError extends BaseFetchError {
  constructor(message, url = '') {
    super([message], null, 0, url);
    if(localStorage.getItem("reload_attempt") !== null){
      const one_minute = (1000 * 60);
      const one_minute_ago = Date.now() - one_minute;
      const last_reload_attempt = localStorage.getItem("reload_attempt");
      if((last_reload_attempt - one_minute_ago) < 0){
        console.log('Network Error. Browser will reload in 1 second');
        setTimeout(function(){
          localStorage.setItem("reload_attempt", Date.now());
          window.location.reload();
        }, 1000);
      }

    }else{
      console.log('Network Error. Browser will reload in 1 second');
      setTimeout(function(){
        localStorage.setItem("reload_attempt", Date.now());
        window.location.reload();
      }, 1000);
    }
  }
}

/*
 * HTTP error that does not contain JSON data
 * in response, such as 404 not found or 500 server error
 */
export class HttpError extends BaseFetchError {
  constructor(message, statusCode, url = '') {
    super([message], null, statusCode, url);
  }
}

/**
 * @param url {string} - url path to which to make the request
 * @param config {object} - config object for the fetch request
 *            config => {
 *                        method: string, POST|PUT|GET|DELETE
 *                        mode: string, cors policy
 *                        headers: object,
 *                        body: json string
 *                      }
 * @param responseType {string} - how to parse the response, can be arrayBuffer|blob|formData|json|text
 */
export async function callApi(url, config = { headers: {} }, responseType = 'json') {
  const isFile = config.body instanceof FormData; // eslint-disable-line no-undef
  let response;
  try {
    response = await fetch(url, config);
  } catch (e) {
    throw new NetworkError(e.message);
  }
  if (response.ok) {
    if (response.body) {
      return isFile || config.method === DELETE
        ? new Promise(resolve => resolve({}))
        : response[responseType]();
    }
  } else {
    switch (response.status) {
      case 400:
      {
        const responseBody = await response.json();
        if (responseBody && responseBody.title
          && API_ERROR_MESSAGES.FILE_NAME_TAKEN === responseBody.title) {
          throw new HttpError(responseBody.title, response, url);
        }

        throw new HttpError('Bad request. Please try again.', response, url);
      }
      case 401:
        throw new HttpError('Username or password is incorrect.', response.status, url);
      case 403:
        throw new HttpError('User forbidden.', response.status, url);
      case 404:
        throw new HttpError(`Not found: ${url}`, response.status, url);
      case 500:
        throw new HttpError('Server error.', response.status, url);
      default:
        throw new HttpError(`Error: ${response.status} ${response.statusText}`, response.status, url);
    }
  }

  return null;
}

function _getConfig(method, authToken, data, isFile) {
  const config = {
    method,
    headers: isFile
      ? {}
      : {
        'content-type': 'application/json',
      },
    body: isFile ? data : JSON.stringify(data),
  };
  if (authToken) {
    config.headers.authorization = `Bearer ${authToken}`;
  }
  return config;
}

export function get(path, authToken) {
  if (authToken) {
    return callApi(path, { headers: { authorization: `Bearer ${authToken}` } });
  }
  return callApi(path);
}

export function post(path, authToken, data, isFile = false) {
  return callApi(path, _getConfig(POST, authToken, data, isFile));
}

export function put(path, authToken, data) {
  return callApi(path, _getConfig(PUT, authToken, data));
}

export function del(path, authToken) {
  return callApi(path, _getConfig(DELETE, authToken));
}

export default {
  get,
  post,
  put,
  del,
};



// WEBPACK FOOTER //
// ./src/common/services/api.service.js